import { css } from "lit";

export const nightGlobalStyles = css`
    html {
        /* inverted MIDAS definitions - see https://midas.maersk-digital.net/visual-design-language/ */
        --neutrals-grey-01: #FFFFFF;
        --neutrals-grey-01-rgb: 255, 255, 255;
        --neutrals-grey-02: #F8F8F8;
        --neutrals-grey-03: #EEEEEE;
        --neutrals-grey-04: #DBDBDB;
        --neutrals-grey-04-rgb: 219, 219, 219;
        --neutrals-grey-05: #CFCFCF;
        --neutrals-grey-06: #B2B2B2;
        --neutrals-grey-06-rgb: 178, 178, 178;
        --neutrals-grey-07: #878787;
        --neutrals-grey-08: #535353;
        --neutrals-grey-09: #353535;
        --neutrals-grey-10: #141414;
        --neutrals-grey-10-rgb: 20, 20, 20;


        --primary-blue-01: #00233D;
        --primary-blue-02: #003D6D;
        --primary-blue-04: #0072AA;
        --primary-blue-05: #058EC6;
        --primary-blue-05-rgb: 5, 142, 198;
        --primary-blue-07: #42AFD6;
        --primary-blue-10: #B5E0F4;

        --warning-yellow-7: #FFB302;
        --warning-yellow-8: #FFC319;
        --warning-yellow-10: #FFDE74;
        --warning-yellow-10-rgb: 255, 222, 116;
        
        --negative-red-6: #F9381C;
        --negative-red-10: #FFEDED;

        --heading-6-size: 16px;
        --heading-6-height: 24px;
        --heading-6-weight: bold;

        --paragraph-size: 14px;
        --paragraph-height: 20px;
        --paragraph-weight: normal;

        /* Voyage Specific */
        --voyage-error-red: #FF0000;
        --voyage-error-red-rgb: 255, 0, 0;
        --voyage-good-green: #27AE60;
        --voyage-good-green-rgb: 39, 174, 96;

        --route1-color: #009E59;
        --route1-transparent-bg-color: #009E5912;
        --route2-color: #006CB6;
        --route2-transparent-bg-color: #006CB612;
        --route3-color: #A91ADB;
        --route3-transparent-bg-color: #A91ADB12;

        --approved-plan-color: var(--voyage-good-green);

        --timeline-current-time-color: var(--voyage-good-green);
        --timeline-bg-color: var(--neutrals-grey-09);
        --timeline-tick-color: var(--neutrals-grey-02);
        --timeline-fg-color: var(--neutrals-grey-01);
        --timeline-button-selected-color: var(--neutrals-grey-04);
        --timeline-divider-color: var(--neutrals-grey-08);

        --tooltip-text-color: var(--neutrals-grey-01);

        --voy-alert-message-background-color: #FFEDED;

        --voyage-info-warn-background-color: #C03030;

        --voyage-performance-chart-simulated-color: #59A9CE;

        --waypoint-fill-color: #FFFFFF80;

        --waypoint-editor-invalid-coordinate: #E21D44;
        --waypoint-editor-new-coordinate: #1D8BE2;
        --waypoint-editor-remove-button-color: #FF0000;
        --waypoint-editor-revert-button: #828282;
        --waypoint-editor-coordinates-text: #000000;
        --waypoint-editor-delete-button-color: #FF0000;

        --performance-viewer-actual-color: #FF0000;
        --performance-viewer-simulated-color: #5ACFFF;
        --performance-viewer-threshold-color: #F456D5;
        --performance-viewer-graph-border-color: rgba(0, 0, 0, 0.3);
        --performance-viewer-nextleg-color: #FF9A1E;
        --performance-viewer-range-color: #000000;
        --performance-viewer-dial-background: #FFFFFF;
        --performance-viewer-graph-background: rgba(53,53,53,0.5);
        --performance-viewer-graph-background-planning: rgba(0, 0, 0, 0.5);
        --performance-viewer-dial-text-color: #FFFFFF;
        
        --chart-shore-color: #CEC67B;
        --chart-intertidal-color: #8BBE93;
        --chart-very-shallow-color: #83C2E7;
        --chart-medium-shallow-color: #A6D4E8;
        --chart-medium-water-color: #C8E3D5;
        --chart-deep-water-color: #DFF7E7;

        --component-heading-ff: MaerskHeadline;
        --component-heading-size: var(--heading-6-size, 20px);
        --component-heading-height: var(--heading-6-height, 20px);
        --component-heading-weight: var(--heading-6-weight, normal);

        --component-paragraph-ff: MaerskText;
        --component-paragraph-size: var(--paragraph-size, 12px);
        --component-paragraph-height: var(--paragraph-height, 12px);
        --component-paragraph-weight: var(--paragraph-weight, normal);

        --enp-layers-chapterBorderColor: #535353;
        --enp-layers-fill-color: rgba(255, 255, 255, 0.07);
        --enp-layers-bookBorderColor: #FF00FF;

        --nav-areas-layers-color: #FF00FF;
        --nav-areas-layers-fill-color: rgba(255, 255, 255, 0.07);

        --whale-zones-layers-border-color: #FF00FF;
        --whale-zones-layers-fill-color: rgba(255, 255, 255, 0.07);
        --whale-zones-layers-restricted-color: #FF0000;
        --whale-zones-layers-avoid-color: #FFC319;

        --marpol-layer-border-color: #FF00FF;
        --marpol-layer-fill-color: rgba(255, 255, 255, 0.07);

        --eca-seca-layer-border-color: #FF00FF;
        --eca-seca-layer-fill-color: rgba(255, 255, 255, 0.07);

        --bwm-layer-border-color: #FF00FF;
        --bwm-layer-fill-color: rgba(255, 255, 255, 0.07);

        --restricted-areas-layer-border-color: #B80012;
        --restricted-areas-layer-corridor-color: #00008B;
        --restricted-areas-layer-fill-color: rgba(184, 0, 18, 0.15);

        --schedule-edit-omit-port: #FF0000;
        --schedule-edit-warning-header: #828282;
        --schedule-edit-warning-background: rgba(255, 222, 116, 0.63);
        --schedule-edit-warning-background-border: #FFC319;

        --voyage-past-trail-popup-background: rgba(53, 53, 53, 0.70);
        --voyage-past-trail-marker: #545454;
        --voyage-past-trail-line: #545454;
        --voyage-past-trail-highlighted-marker:  #7e7e7e;
    }
`;
